import React from 'react';

import SelectionTextBox from './SelectionTextBox';
import {DomainFilter, Filter, PartitionFilter, PropertyFilter, PropFilterVariable, UnitSystem} from '../utils/Filter';
import {randomChoice} from '../utils/utils';
import './FilterWidget.css';
import { Domain } from './Constants';

interface FilterWidgetProps {
    width: number;
    example: string;
    domains: Domain[];
    sel_domain: Domain;
    units: UnitSystem;
    allow_states: boolean;

    filters: Record<string, Filter>;

    onfilterchange: Function;
    ondomainchange: (new_domain: Domain) => void;
}

class FilterWidget extends React.Component<FilterWidgetProps> {
    constructor(props: FilterWidgetProps) {
        super(props);

        this.partitionClick = this.partitionClick.bind(this);
    }

    partitionClick(event: React.MouseEvent) : void {
        const target = event?.target;
        if (!(target instanceof HTMLElement)) {
            throw "Partition click target is of type " + (typeof target) + ", expected HTMLElement";
        }

        const clicked_part = target.childNodes[0].textContent;

        if (clicked_part === null) {
            throw "Partition click target has no text";
        }

        const filter = this.props['filters']['partition'];

        if (!(filter instanceof PartitionFilter)) {
            throw "Partition filter is of type " + (typeof filter) + ", expected PartitionFilter";
        }

        this.props['onfilterchange']({'partition': filter.togglePartition(clicked_part)});
    }

    filterSuggestions(entered_txt: string) : {possibilities: PropFilterVariable, autocomplete: string | null} {
        const parsed = PropertyFilter.getPossibilities(entered_txt, this.props['allow_states']);

        let suggestions = {
            'possibilities': parsed.getIncompleteEntries(),
            'autocomplete': parsed.getAutocomplete(),
        };

        return suggestions;
    }

    filterSuggestionsBuild(suggestions: {possibilities: PropFilterVariable, autocomplete: string | null}, width: number) {
        const possibilities = suggestions['possibilities'];

        let suggestions_jsx: JSX.Element | JSX.Element[] = <div></div>;
        let cls = "textbox-suggestions";
        const max_options = 8;
        let options_overflow = false;

        if (possibilities['type'] == 'enum') {
            let options = [...possibilities['options']];
            if (options.length > max_options) {
                options_overflow = true;
                let keep_options: string[] = [];
                for (let idx = 0; idx < max_options; idx++) {
                    const opt = randomChoice(options)
                    keep_options.push(opt);
                    options = options.filter(el => el != opt);
                }
                options = keep_options;
                options.sort();
            }

            suggestions_jsx = options.map(sugg => <p key={sugg}>{sugg}</p>);
        }
        else if (possibilities['type'] == 'number') {
            let str = "[Number]";
            const units = possibilities['units'](this.props['units']);
            if (units != '') {
                str += ' ' + units;
            }

            suggestions_jsx = <p>{str}</p>;
        }
        else if (possibilities['type'] == 'string') {
            let str = "[String]";

            suggestions_jsx = <p>{str}</p>;
        }

        if (possibilities['type'] == 'enum' && possibilities['options'].length == 0) {
            cls += " invalid";
        }

        let tab_complete_jsx = <span></span>;
        if (suggestions['autocomplete'] !== null) {
            tab_complete_jsx = <span style={{'marginLeft': '2px'}}>TAB to complete</span>;
        }

        let overflow_jsx = <span></span>;
        if (options_overflow) {
            overflow_jsx = <span style={{'marginLeft': '2px'}}>and others ...</span>;
        }

        return (
            <div className={cls} style={{'width': width + 'px'}}>
                <span style={{'paddingTop': '3px', 'paddingBottom': '3px'}}>Suggestions:</span>
                {suggestions_jsx}
                {tab_complete_jsx}
                {overflow_jsx}
            </div>
        );
    }

    filterParse(filter_str: string) : PropertyFilter | null {
        return PropertyFilter.fromString(filter_str, this.props['units'], this.props['allow_states']);
    }

    filterSearch(filters: Filter[]) : void {
        let filter_obj: Record<string, (Filter | null)> = Object.fromEntries(Object.keys(this.props['filters'])
            .filter(f => f != 'partition')
            .map(f => [f, null]));

        filters.forEach((filt: Filter) => {
            if (!(filt instanceof PropertyFilter)) {
                throw "Found filter instance in search of type " + (typeof filt) + ", expected PropertyFilter";
            }
            filter_obj[filt.variable] = filt;
        });

        this.props['onfilterchange'](filter_obj);
    }

    ondomainchange() : void {
        if (this.props['ondomainchange'] !== undefined) {
            const dom_control = document.getElementById('domain-control');
            if (!(dom_control instanceof HTMLSelectElement)) {
                throw "#domain-control is of type " + (typeof dom_control) + ", expected HTMLSelectElement";
            }

            const dom_value = dom_control.value as Domain;
            this.props['ondomainchange'](dom_value);
        }
    }

    render() : JSX.Element {
        const part_filter = this.props['filters']['partition'];
        if (!(part_filter instanceof PartitionFilter)) {
            throw "Partition filter is of type " + (typeof part_filter) + ", expected PartitionFilter";
        }

        const width = Math.min(800, (this.props['width'] - 520) * 0.8);
        const filters = Object.values(this.props['filters']).filter((f: Filter) => f.display(this.props['units']) !== null);

        return (
            <div id='filter-controls'>
                <div className='control'>
                    Domain:&nbsp;
                    <select 
                        id='domain-control'
                        defaultValue={this.props['sel_domain']}
                        onChange={this.ondomainchange.bind(this)}>
                        {
                            this.props['domains'].map((dom_name: string) => {
                                return <option key={dom_name}>{dom_name}</option>
                            })
                        }
                    </select>
                </div>
                <div className='control'>
                    <ul id='partition-control'>
                        {part_filter.part.label_str}:
                        {
                            part_filter.part.part_keys.map((k: string) => {
                                return <li key={k} style={part_filter.getPartDisplayStyle(k)} onClick={this.partitionClick}>{k}</li>
                            })
                        }
                    </ul>
                </div>
                <div className='control'>
                    Filters:&nbsp;
                    <SelectionTextBox
                        content={filters}
                        placeholder={'e.g., ' + this.props['example']}
                        onselectionupdate={this.filterSearch.bind(this)}
                        parser={this.filterParse.bind(this)}
                        width={width}
                        suggestion_callback={this.filterSuggestions.bind(this)}
                        suggestion_builder={this.filterSuggestionsBuild.bind(this)}
                        max_selections={10}
                        display_args={[this.props['units']]}
                    />
                </div>
            </div>
        );
    }
}

export default FilterWidget;